import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Mic, Square, ClipboardCopy, Trash2, FileAudio } from "lucide-react";

const API = `${window.location.origin}/api`;
const GOLD = "#C4A671";
const CHUNK = 512 * 1024;

const inputCls =
  "w-full bg-[#111] border border-[#2a2a2a] px-4 py-3 text-[13.5px] text-[#F2EFE8] outline-none focus:border-[#C4A671] transition-colors";

async function compressImage(file) {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.8));
    return blob || file;
  } catch {
    return file;
  }
}

const blobToB64 = (blob) =>
  new Promise((res) => {
    const r = new FileReader();
    r.onloadend = () => res(String(r.result).split(",")[1]);
    r.readAsDataURL(blob);
  });

async function uploadAndTranscribe(blob, filename, authHeader, setPhase) {
  const total = Math.max(1, Math.ceil(blob.size / CHUNK));
  const { data: init } = await axios.post(`${API}/admin/studio/upload/init`, { filename, total_chunks: total }, authHeader);
  for (let i = 0; i < total; i++) {
    await axios.put(`${API}/admin/studio/upload/${init.upload_id}/chunk/${i}`, blob.slice(i * CHUNK, (i + 1) * CHUNK), {
      ...authHeader, headers: { ...authHeader.headers, "Content-Type": "application/octet-stream" },
    });
    setPhase(`Caricamento ${Math.round(((i + 1) / total) * 100)}%…`);
  }
  setPhase("Trascrizione Whisper in corso…");
  const { data } = await axios.post(`${API}/admin/studio/transcribe/${init.upload_id}`, {}, authHeader);
  return data.transcript;
}

function OutputCard({ label, text, testId }) {
  if (!text) return null;
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copiato negli appunti.`);
  };
  return (
    <div className="border border-[#1f1f1f] bg-[#0F0E0B] p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="overline text-[9px]" style={{ color: GOLD }}>{label}</span>
        <button onClick={copy} data-testid={`${testId}-copy`}
          className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#7A7568] hover:text-[#C4A671] transition">
          <ClipboardCopy size={13} /> Copia
        </button>
      </div>
      <p className="text-[13px] leading-[1.75] text-[#C9C3B5] whitespace-pre-wrap">{text}</p>
    </div>
  );
}

export default function StudioPanel({ authHeader }) {
  const [brief, setBrief] = useState("");
  const [transcript, setTranscript] = useState("");
  const [imageB64, setImageB64] = useState(null);
  const [english, setEnglish] = useState(false);
  const [preview, setPreview] = useState(null);
  const [phase, setPhase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [recording, setRecording] = useState(false);
  const photoRef = useRef(null);
  const mediaRef = useRef(null);
  const recRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/studio/history`, authHeader);
      setHistory(data?.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadHistory(); }, []); // eslint-disable-line

  const pickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const blob = await compressImage(f);
    setImageB64(await blobToB64(blob));
    setPreview(URL.createObjectURL(blob));
  };

  const pickMedia = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const t = await uploadAndTranscribe(f, f.name, authHeader, setPhase);
      setTranscript((p) => (p ? `${p}\n${t}` : t));
      toast.success("Nota trascritta.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Trascrizione non riuscita.");
    } finally { setBusy(false); setPhase(""); }
  };

  const toggleRec = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: "audio/webm" });
        setBusy(true);
        try {
          const t = await uploadAndTranscribe(blob, "nota-vocale.webm", authHeader, setPhase);
          setTranscript((p) => (p ? `${p}\n${t}` : t));
          toast.success("Nota vocale trascritta.");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Trascrizione non riuscita.");
        } finally { setBusy(false); setPhase(""); }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      toast.info("Registrazione in corso — riclicca per fermare.");
    } catch {
      toast.error("Microfono non disponibile.");
    }
  };

  const generate = async () => {
    if (!brief.trim() && !transcript.trim() && !imageB64) {
      toast.error("Fornisci almeno un input: brief, nota vocale o fotografia.");
      return;
    }
    setBusy(true);
    setPhase("L'agente sta scrivendo i contenuti… (30-60s)");
    try {
      const { data } = await axios.post(`${API}/admin/studio/generate`, {
        brief: brief.trim() || null,
        transcript: transcript.trim() || null,
        image_b64: imageB64,
        english,
      }, authHeader);
      setResult(data);
      loadHistory();
      toast.success("Contenuti pronti.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generazione non riuscita.");
    } finally { setBusy(false); setPhase(""); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Eliminare questo contenuto dallo storico?")) return;
    try {
      await axios.delete(`${API}/admin/studio/${id}`, authHeader);
      setHistory((p) => p.filter((x) => x.id !== id));
      if (result?.id === id) setResult(null);
    } catch { toast.error("Errore eliminazione."); }
  };

  const out = result?.outputs;

  return (
    <div className="max-w-4xl" data-testid="studio-panel">
      <h2 className="font-serif-display text-[22px] text-[#F2EFE8] mb-1">✎ Studio Contenuti</h2>
      <p className="text-[13px] text-[#7A7568] mb-6">
        Una foto, una nota vocale (o video) o due righe — l&apos;agente genera post LinkedIn (Maison + Claudio),
        caption Instagram e articolo blog nel tono della maison.
      </p>

      <div className="grid md:grid-cols-[1fr_220px] gap-5 mb-5">
        <div className="flex flex-col gap-3">
          <textarea
            data-testid="studio-brief"
            className={`${inputCls} resize-none`} rows={3}
            placeholder="Brief veloce (es. 'vendemmia notturna del Frappato, luna piena, prime bottiglie di Blanche 2026')…"
            value={brief} onChange={(e) => setBrief(e.target.value)} maxLength={3000}
          />
          <textarea
            data-testid="studio-transcript"
            className={`${inputCls} resize-none`} rows={4}
            placeholder="Trascrizione nota vocale/video (si riempie da sola, puoi correggerla)…"
            value={transcript} onChange={(e) => setTranscript(e.target.value)} maxLength={8000}
          />
          <div className="flex flex-wrap gap-2">
            <input ref={mediaRef} type="file" accept="audio/*,video/mp4,video/webm" className="hidden" onChange={pickMedia} data-testid="studio-media-input" />
            <button data-testid="studio-record" onClick={toggleRec} disabled={busy}
              className={`flex items-center gap-2 px-4 py-2.5 text-[9.5px] uppercase tracking-[0.2em] border transition ${recording ? "border-[#c0392b] text-[#e07b6d]" : "border-[#C4A671]/50 text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A]"}`}>
              {recording ? <><Square size={13} /> Ferma registrazione</> : <><Mic size={13} /> Registra nota vocale</>}
            </button>
            <button data-testid="studio-media-pick" onClick={() => mediaRef.current?.click()} disabled={busy}
              className="flex items-center gap-2 px-4 py-2.5 text-[9.5px] uppercase tracking-[0.2em] border border-[#2a2a2a] text-[#7A7568] hover:text-[#C9C3B5] transition">
              <FileAudio size={13} /> Carica audio / video
            </button>
          </div>
        </div>
        <div>
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={pickPhoto} data-testid="studio-photo-input" />
          <button data-testid="studio-photo-pick" onClick={() => photoRef.current?.click()}
            className="w-full border border-[#2a2a2a] hover:border-[#C4A671]/50 transition overflow-hidden">
            {preview ? (
              <img src={preview} alt="Anteprima" className="w-full aspect-[4/3] object-cover" />
            ) : (
              <span className="flex flex-col items-center justify-center gap-2 py-12 text-[#7A7568]">
                <Camera size={22} strokeWidth={1.2} style={{ color: GOLD }} />
                <span className="overline text-[8.5px]">Fotografia (opzionale)</span>
              </span>
            )}
          </button>
          {preview && (
            <button onClick={() => { setPreview(null); setImageB64(null); }}
              className="mt-2 overline text-[8.5px] text-[#7A7568] hover:text-[#e07b6d] transition">✕ Rimuovi foto</button>
          )}
        </div>
      </div>

      <label className="flex items-center gap-2.5 cursor-pointer select-none mb-4" data-testid="studio-english-toggle">
        <input
          type="checkbox"
          checked={english}
          onChange={(e) => setEnglish(e.target.checked)}
          className="accent-[#C4A671] w-4 h-4"
        />
        <span className="text-[11.5px] text-[#C9C3B5]">
          🌍 Genera anche in inglese <span className="text-[#7A7568]">(LinkedIn/Instagram internazionali)</span>
        </span>
      </label>

      <button
        data-testid="studio-generate"
        onClick={generate}
        disabled={busy}
        className="bg-[#C4A671] text-[#0A0A0A] px-8 py-3.5 text-[10.5px] tracking-[0.24em] uppercase font-medium hover:bg-white transition disabled:opacity-40"
      >
        {busy ? phase || "Lavorazione…" : "✦ Genera i contenuti"}
      </button>
      {busy && phase && <p className="mt-2 text-[11.5px] italic text-[#8B7A55]">{phase}</p>}

      {out && (
        <div className="mt-8 flex flex-col gap-4" data-testid="studio-results">
          <OutputCard label="LinkedIn · Pagina Maison" text={out.linkedin_maison} testId="out-linkedin-maison" />
          <OutputCard label="LinkedIn · Claudio Pucci (personale)" text={out.linkedin_claudio} testId="out-linkedin-claudio" />
          <OutputCard label="Instagram · Caption" text={out.instagram} testId="out-instagram" />
          <OutputCard label="LinkedIn · Maison — English" text={out.linkedin_maison_en} testId="out-linkedin-maison-en" />
          <OutputCard label="LinkedIn · Claudio Pucci — English" text={out.linkedin_claudio_en} testId="out-linkedin-claudio-en" />
          <OutputCard label="Instagram · Caption — English" text={out.instagram_en} testId="out-instagram-en" />
          <OutputCard label={`Blog / PR · ${out.blog_title}`} text={out.blog_body} testId="out-blog" />
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-10">
          <div className="overline text-[9.5px] mb-3 text-[#8B7A55]">Storico Studio</div>
          <div className="border border-[#1f1f1f] divide-y divide-[#1f1f1f]">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`studio-history-${h.id}`}>
                <button onClick={() => setResult(h)} className="flex-1 min-w-0 text-left text-[12.5px] text-[#C9C3B5] hover:text-[#C4A671] transition truncate">
                  {h.outputs?.blog_title || h.brief || h.transcript || "Contenuto"}
                </button>
                <span className="text-[10.5px] text-[#5a5548] shrink-0">{new Date(h.created_at).toLocaleDateString("it-IT")}</span>
                <button onClick={() => removeItem(h.id)} className="text-[#5a5a5a] hover:text-[#c0392b] transition shrink-0" data-testid={`studio-delete-${h.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
